import React, { useState, useEffect } from 'react';

import { Text } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import AIPromptInput from './AIPromptInput';
import UseCaseSelector from './UseCaseSelector';
import FilmStripSlider from './components/FilmStripSlider';
import VideoPlayer from './components/VideoPlayer';
import { UseCase } from './utils/constants';
import {
  MAX_SELECTION_DURATION,
  VideoMetadata,
  VideoSelection,
} from './utils/videoUtils';

interface VideoEditorUIProps {
  src?: string;
  videoMetadata?: VideoMetadata | null;
  isPlaying: boolean;
  onPlayPause: () => void;
  selectedUseCase: string | null;
  onUseCaseSelect: (id: string) => void;
  useCases: UseCase[];
  prompt: string;
  onPromptChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onGenerateVideo: (selection?: VideoSelection) => void;
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
}

const VideoEditorUI = ({
  src,
  videoMetadata,
  isPlaying,
  onPlayPause,
  selectedUseCase,
  onUseCaseSelect,
  useCases,
  prompt,
  onPromptChange,
  onGenerateVideo,
  suggestions,
  onSuggestionClick,
}: VideoEditorUIProps) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [selection, setSelection] = useState<VideoSelection>(() => {
    if (!videoMetadata) {
      return {
        startTime: 0,
        endTime: MAX_SELECTION_DURATION,
        duration: MAX_SELECTION_DURATION,
      };
    }
    return {
      startTime: 0,
      endTime: Math.min(videoMetadata.duration, MAX_SELECTION_DURATION),
      duration: Math.min(videoMetadata.duration, MAX_SELECTION_DURATION),
    };
  });

  const handleTimeUpdate = (time: number) => {
    setCurrentTime(time);
  };

  const handleSelectionChange = (newSelection: VideoSelection) => {
    setSelection(newSelection);
  };

  const handleVideoTimeUpdate = (time: number) => {
    setCurrentTime(time);
  };

  const handleGenerateClick = () => {
    onGenerateVideo(selection);
  };

  // Auto-play when modal opens and video metadata is available
  useEffect(() => {
    if (videoMetadata && src) {
      // Start playing automatically when the modal opens
      // The video will start from the selection start time due to VideoPlayer's selection logic
      const timer = setTimeout(() => {
        onPlayPause();
      }, 500); // Small delay to ensure video is ready

      return () => clearTimeout(timer);
    }
  }, [videoMetadata, src]);

  if (!src || !videoMetadata) {
    return (
      <div
        className="flex flex-col items-center px-24"
        style={{
          backgroundColor: '#1A1C1E99',
          opacity: 60,
          backdropFilter: 'blur(80px)',
          width: '1078px',
          height: '100%',
        }}
      >
        <div className="mt-8 flex gap-2 items-center mb-8">
          <img
            className="h-8 w-8"
            src={`${__ASSETS_COMMON_IMAGES_URL__}/logos/storyblocks-icon.svg`}
            alt="Storyblocks Logo"
          />
          <Text fontSize="xl" className="m-0">
            Edit with AI
          </Text>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <Text fontSize="lg" className="text-white">
            {!src ? 'No video selected' : 'Loading video...'}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mt-8 flex gap-2 items-center mb-8">
        <img
          className="h-8 w-8"
          src={`${__ASSETS_COMMON_IMAGES_URL__}/logos/storyblocks-icon.svg`}
          alt="Storyblocks Logo"
        />
        <Text fontSize="xl" className="m-0">
          Edit with AI
        </Text>
      </div>

      <div className="w-full flex-1 flex flex-col">
        <Text fontSize="sm" className="text-white mb-6">
          Pick your favorite 5 seconds to edit:
        </Text>

        {/* Video Player and Filmstrip Container - no gap */}
        <div className="flex flex-col w-full">
          <VideoPlayer
            src={src}
            selection={selection}
            isPlaying={isPlaying}
            onPlayPause={onPlayPause}
            onTimeUpdate={handleTimeUpdate}
            currentTime={currentTime}
            className="w-full"
          />

          <FilmStripSlider
            src={src}
            videoMetadata={videoMetadata}
            selection={selection}
            onSelectionChange={handleSelectionChange}
            onVideoTimeUpdate={handleVideoTimeUpdate}
            onPlayPause={onPlayPause}
            isPlaying={isPlaying}
          />
        </div>
      </div>

      <UseCaseSelector
        useCases={useCases}
        selectedUseCase={selectedUseCase}
        onUseCaseSelect={onUseCaseSelect}
      />

      <AIPromptInput
        prompt={prompt}
        onPromptChange={onPromptChange}
        onGenerateVideo={handleGenerateClick}
        suggestions={suggestions}
        onSuggestionClick={onSuggestionClick}
      />
    </>
  );
};

export default VideoEditorUI;
